// Centralized app configuration for the local-export build.
// Runtime environment selection is loaded from public/poof.config.json by poofClient.ts.

import { getLoadedConfig, getResolvedEnv, isMockAuth } from './poofClient';

const DEFAULT_API_URL = 'https://api.tarobase.com';
const DEFAULT_AUTH_API_URL = 'https://auth.tarobase.com';
const DEFAULT_WS_API_URL = 'wss://api.tarobase.com/ws/v2';
const DEFAULT_MOCK_ADDRESS = 'HKbZbRR7jWWR5VRN8KFjvTCHEzJQgameYxKQxh2gPoof';
const PRODUCTION_CHECKOUT_ORIGIN = 'https://pay.poofy.ai';

function getQueryParams(): URLSearchParams | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search);
}

function queryAppId(): string | undefined {
  return getQueryParams()?.get('appId')?.trim() || undefined;
}

function queryMockAddress(): string | null {
  return getQueryParams()?.get('mockAddress')?.trim() || null;
}

function selectedEnvConfig() {
  return getResolvedEnv()?.config;
}

function selectedEnvName(): string | undefined {
  return getResolvedEnv()?.name;
}

function selectedConfig() {
  return getLoadedConfig();
}

function getAppId(): string | undefined {
  return (
    queryAppId() ||
    selectedEnvConfig()?.tarobaseAppId ||
    selectedEnvConfig()?.appId ||
    import.meta.env.VITE_TAROBASE_APP_ID
  );
}

function getApiUrl(): string {
  return selectedEnvConfig()?.apiUrl || selectedConfig()?.apiUrl || import.meta.env.VITE_API_URL || DEFAULT_API_URL;
}

function getAuthApiUrl(): string {
  return (
    selectedEnvConfig()?.authApiUrl ||
    selectedConfig()?.authApiUrl ||
    import.meta.env.VITE_AUTH_API_URL ||
    DEFAULT_AUTH_API_URL
  );
}

function getWsApiUrl(): string {
  return (
    selectedEnvConfig()?.wsApiUrl ||
    selectedEnvConfig()?.wsUrl ||
    selectedConfig()?.wsApiUrl ||
    selectedConfig()?.wsUrl ||
    import.meta.env.VITE_WS_API_URL ||
    DEFAULT_WS_API_URL
  );
}

function getChain(): string {
  if (import.meta.env.VITE_CHAIN) return import.meta.env.VITE_CHAIN;
  return selectedEnvName() === 'draft' ? 'offchain' : 'solana_mainnet';
}

function getRpcUrl(): string | undefined {
  if (import.meta.env.VITE_RPC_URL) return import.meta.env.VITE_RPC_URL;
  const appId = getAppId();
  if (getChain() === 'offchain' && appId) {
    return `${getApiUrl()}/app/${appId}/rpc`;
  }
  return undefined;
}

function normalizeBackendBase(raw: string): string {
  const trimmed = raw.trim().replace(/\/+$/, '');
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const protocol = trimmed.includes('localhost') || trimmed.startsWith('127.0.0.1') ? 'http' : 'https';
  return `${protocol}://${trimmed}`;
}

function normalizeFrontendBase(raw: string): string {
  const trimmed = raw.trim().replace(/\/+$/, '');
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const protocol = trimmed.includes('localhost') || trimmed.startsWith('127.0.0.1') ? 'http' : 'https';
  return `${protocol}://${trimmed}`;
}

function isLocalFrontendHost(): boolean {
  if (typeof window === 'undefined') return false;
  return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
}

export function getPartyServerHost(): string {
  const explicit = import.meta.env.VITE_PARTYSERVER_URL;
  if (explicit) return explicit.replace(/^https?:\/\//i, '').replace(/\/+$/, '');

  const backendUrl = selectedEnvConfig()?.backendUrl;
  if (backendUrl) return backendUrl.replace(/^https?:\/\//i, '').replace(/\/+$/, '');

  const appId = getAppId();
  return appId ? `${appId}-api.poof.new` : 'localhost:1999';
}

export const PARTYSERVER_URL = getPartyServerHost();

export const TAROBASE_CONFIG = {
  get appId() {
    return getAppId();
  },
  get chain() {
    return getChain();
  },
  get rpcUrl() {
    return getRpcUrl();
  },
  get authMethod() {
    return import.meta.env.VITE_AUTH_METHOD || 'phantom';
  },
  get wsApiUrl() {
    return getWsApiUrl();
  },
  get apiUrl() {
    return getApiUrl();
  },
  get authApiUrl() {
    return getAuthApiUrl();
  },
  get mockAuth() {
    return isMockAuth();
  },
  get mockAddress() {
    return queryMockAddress() || DEFAULT_MOCK_ADDRESS;
  },
};

export function getPoofAPIHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const appId = getAppId();
  if (getChain() === 'offchain' && appId) {
    headers['x-tarobase-app-id'] = appId;
  }
  return headers;
}

export const POOF_OAUTH_URL = import.meta.env.VITE_POOF_OAUTH_URL || 'https://oauth.poof.new';

export const UI_CONFIG = {};

export function getPoofAPIUrl(path?: string): string {
  // On a local dev/test host keep API paths root-relative so the same-origin Vite
  // proxy (vite.config.ts → /api) forwards them server-side to the configured
  // backend. Returning an absolute backend URL makes the browser issue a
  // cross-origin request that the backend's exact-match CORS allowlist (only
  // localhost:3000/3001 + the .poof.new hosts, never the harness's ephemeral
  // dev-server port) rejects at the OPTIONS preflight with 403. The local harness
  // sets VITE_PARTYSERVER_URL to configure that proxy target, so the presence of
  // VITE_PARTYSERVER_URL must NOT force an absolute URL here.
  if (path?.startsWith('/') && isLocalFrontendHost()) {
    return path;
  }
  const explicit = import.meta.env.VITE_PARTYSERVER_URL;
  const base = normalizeBackendBase(explicit || selectedEnvConfig()?.backendUrl || getPartyServerHost());
  return path ? `${base}${path}` : base;
}

export function getCheckoutUrl(path: string): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const envName = selectedEnvName();
  const configuredUrl = selectedEnvConfig()?.url;
  const fallback = typeof window !== 'undefined' ? window.location.origin : '';
  const baseInput = configuredUrl || fallback;
  const base = envName === 'production'
    ? PRODUCTION_CHECKOUT_ORIGIN
    : baseInput
      ? normalizeFrontendBase(baseInput)
      : '';
  return `${base}${normalizedPath}`;
}
